/**
 * Binds page visibility (web) and AppState (native; RN-web derives it from
 * visibility too) to the connection. Registered once per connection — it
 * never re-runs on connection-state changes, so a close can only be followed
 * by the backoff timer, not by an immediate visibility-driven reconnect.
 */

import type { WebSocketConnection } from './connection';

export interface VisibilitySource {
  readonly hidden: boolean;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface AppStateSource {
  addEventListener(type: 'change', listener: (state: string) => void): { remove(): void };
}

export function bindPageLifecycle(
  connection: Pick<WebSocketConnection, 'resume' | 'setPageHidden'>,
  sources: { document?: VisibilitySource | null; appState?: AppStateSource | null }
): () => void {
  const cleanups: (() => void)[] = [];

  const doc = sources.document;
  if (doc) {
    const onVisibility = () => {
      if (doc.hidden) {
        connection.setPageHidden(true);
      } else {
        connection.resume();
      }
    };
    // Initial state only — the mount effect owns the first connect.
    connection.setPageHidden(doc.hidden);
    doc.addEventListener('visibilitychange', onVisibility);
    cleanups.push(() => doc.removeEventListener('visibilitychange', onVisibility));
  }

  if (sources.appState) {
    const subscription = sources.appState.addEventListener('change', (state) => {
      if (state === 'active') connection.resume();
    });
    cleanups.push(() => subscription.remove());
  }

  return () => cleanups.forEach((cleanup) => cleanup());
}
